import _extends from "@babel/runtime/helpers/extends";
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SkipToMainContent, StyledHeader } from './headerStyles';
import { EmbersHeaderLogo } from './logo/headerLogo';
import { EmbersHeaderOrg } from './org/headerOrg';
import { EmbersHeaderAvatar } from './avatar/headerAvatar';
import { EmbersHeaderNotification } from './notification/headerNotification';
import { generateAvatarInitials } from '../utils/profileUtils';
import { useResetSkipToMain } from '../utils/useResetSkipToMain';
import { PROMETHEAN_BREAKPOINT_SM, PROMETHEAN_BREAKPOINT_NAV_CHANGE } from '../styles/breakpoints/breakpoints';
import { onEnterKeyPress } from '../utils/wcagUtils';
import { APP_MENU_ARIA_ID } from '../appSwitcher/appSwitcher';
export var SKIP_TO_MAIN_CONTENT_ID = 'embers-main';
export var SKIP_COMMAND_RESET_ID = 'embers-skip-to-main-reset';
export var EmbersHeader = function EmbersHeader(props) {
  var _profile$avatar;
  var role = props.role,
    className = props.className,
    style = props.style,
    isModalHeader = props.isModalHeader,
    toggleAppSwitcher = props.toggleAppSwitcher,
    appSwitcherOpen = props.appSwitcherOpen,
    hideAppSwitchIcon = props.hideAppSwitchIcon,
    handleClickLogo = props.handleClickLogo,
    notificationCount = props.notificationCount,
    onNotificationClick = props.onNotificationClick,
    orgsList = props.orgsList,
    currentOrg = props.currentOrg,
    handleOrgDetailsClick = props.handleOrgDetailsClick,
    handleOrgSelect = props.handleOrgSelect,
    scrollWrapperSelector = props.scrollWrapperSelector,
    profile = props.profile,
    handleClickAccount = props.handleClickAccount,
    handleSignOut = props.handleSignOut,
    signedIn = props.signedIn,
    handleSignIn = props.handleSignIn,
    disableSignOut = props.disableSignOut;
  var intl = useIntl();

  /** it should reset skip to main content when the router changed */
  useResetSkipToMain();
  var isMobileWidth = useMediaQuery("(max-width:".concat(PROMETHEAN_BREAKPOINT_SM - 1, "px)"));
  var isTabletPortraitWidth = useMediaQuery("(min-width:".concat(PROMETHEAN_BREAKPOINT_SM, "px) and (max-width:").concat(PROMETHEAN_BREAKPOINT_NAV_CHANGE, "px)"));
  var appSwitchAriaAttributes = {
    role: 'button',
    'aria-label': intl.formatMessage({
      id: 'embers.header.eA.appSwitch',
      defaultMessage: 'Promethean apps menu'
    }),
    'aria-controls': appSwitcherOpen ? APP_MENU_ARIA_ID : undefined,
    'aria-expanded': appSwitcherOpen,
    'aria-haspopup': true
  };
  var renderLogo = function renderLogo() {
    return /*#__PURE__*/React.createElement(EmbersHeaderLogo, {
      handleClickLogo: handleClickLogo
    });
  };
  var featureSetting = {
    appSwitcher: !hideAppSwitchIcon,
    notifications: true,
    org: {
      show: !!(orgsList && signedIn),
      menuWithIcon: false
    },
    profile: {
      hideAccount: false
    }
  };
  if (isMobileWidth) {
    featureSetting.appSwitcher = false;
    featureSetting.notifications = false;
    featureSetting.org.show = false;
    featureSetting.profile.hideAccount = true;
  } else if (isTabletPortraitWidth) {
    featureSetting.notifications = false;
    featureSetting.org.menuWithIcon = true;
  }
  var handleKeyboardOpen = function handleKeyboardOpen(e) {
    if (toggleAppSwitcher) {
      onEnterKeyPress(e, toggleAppSwitcher);
      var listItems = document.querySelectorAll('.EmbersAppSwitcher-item');
      var firstElement = listItems[0];
      setTimeout(function () {
        firstElement === null || firstElement === void 0 || firstElement.focus();
      }, 20);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    tabIndex: -1,
    id: SKIP_COMMAND_RESET_ID
  }), /*#__PURE__*/React.createElement(SkipToMainContent, {
    href: "#".concat(SKIP_TO_MAIN_CONTENT_ID)
  }, intl.formatMessage({
    id: 'embers.header.eA.skip',
    defaultMessage: 'Skip to main content'
  })), /*#__PURE__*/React.createElement(StyledHeader, {
    role: role || 'banner',
    className: classnames('EmbersHeader-root', {
      'EmbersHeader-portal': isModalHeader,
      'EmbersHeader-signedOut': !signedIn,
      'EmbersHeader-mobile': isMobileWidth,
      'EmbersHeader-tablet': isTabletPortraitWidth,
      className: className
    }),
    style: style
  }, isModalHeader ? renderLogo() : /*#__PURE__*/React.createElement(React.Fragment, null, featureSetting.appSwitcher && /*#__PURE__*/React.createElement("div", _extends({}, appSwitchAriaAttributes, {
    className: classnames('EmbersHeader-app', {
      'EmbersHeader-app-appSwitcherOpen': appSwitcherOpen
    }),
    onClick: function onClick() {
      return toggleAppSwitcher === null || toggleAppSwitcher === void 0 ? void 0 : toggleAppSwitcher();
    },
    onKeyDown: handleKeyboardOpen,
    tabIndex: 0
  }), /*#__PURE__*/React.createElement("span", {
    className: "apps-button icon-apps"
  }), /*#__PURE__*/React.createElement("span", {
    className: "apps-button icon-apps-fill"
  })), /*#__PURE__*/React.createElement("div", {
    className: "EmbersHeader-mainContent"
  }, renderLogo(), /*#__PURE__*/React.createElement("div", {
    className: "EmbersHeader-rightContent"
  }, featureSetting.notifications && /*#__PURE__*/React.createElement(EmbersHeaderNotification, {
    count: notificationCount,
    onClick: onNotificationClick
  }), featureSetting.org.show && /*#__PURE__*/React.createElement(EmbersHeaderOrg, {
    orgsList: orgsList,
    currentOrg: currentOrg,
    handleOrgDetailsClick: handleOrgDetailsClick,
    handleOrgSelect: handleOrgSelect,
    notificationCount: notificationCount,
    scrollWrapperSelector: scrollWrapperSelector,
    menuWithIcon: featureSetting.org.menuWithIcon
  }), /*#__PURE__*/React.createElement(EmbersHeaderAvatar, {
    avatarUrl: profile === null || profile === void 0 || (_profile$avatar = profile.avatar) === null || _profile$avatar === void 0 ? void 0 : _profile$avatar.preSignedUrl,
    initials: generateAvatarInitials(profile),
    handleClickAccount: handleClickAccount,
    handleSignOut: handleSignOut,
    signedIn: signedIn,
    handleSignIn: handleSignIn,
    disableSignOut: disableSignOut,
    hideAccount: featureSetting.profile.hideAccount
  }))))));
};